import React, { useEffect, useState } from "react";
import { Trophy, Medal } from "lucide-react";
import { TurfTalk } from "@/api/client";
import { PageHeader, Skeleton } from "@/components/ui-kit";
import { CHAR_COLOR, CHAR_NAME } from "@/lib/tokens";

const SCOPES = [
  { key: "characters", label: "Characters" },
  { key: "global", label: "Global" },
];

export default function PredictPage() {
  const [scope, setScope] = useState("characters");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    TurfTalk.predictorLeaderboard(scope, "all").then((r) => setRows(r.rows || [])).catch(() => setRows([]));
  }, [scope]);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Predict" subtitle="Straight-up, ATS and over/under leaderboards. Exhibition only — not betting advice." />
      <div className="flex gap-2 mb-6">
        {SCOPES.map((s) => (
          <button key={s.key} onClick={() => setScope(s.key)} data-testid={`scope-${s.key}`}
            className={`px-4 h-9 rounded-full text-sm font-display tracking-wide transition-colors ${scope === s.key ? "bg-[#2F7D32] text-white" : "tt-panel text-white/60 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {rows === null ? <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        : rows.length === 0 ? <div className="tt-panel rounded-xl p-10 text-center text-white/40">No graded picks yet this window.</div>
        : (
          <div className="tt-panel rounded-xl overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_repeat(4,minmax(48px,1fr))] gap-2 px-4 py-3 text-[11px] tracking-wide text-white/40 border-b border-[#2A3544]">
              <span>#</span><span>ANALYST</span><span className="text-right">SU</span><span className="text-right">ATS</span><span className="text-right">O/U</span><span className="text-right">PTS</span>
            </div>
            {rows.map((r) => {
              const rec = r.record || {};
              const color = r.is_character ? CHAR_COLOR[r.author_id] : "#8aa0b8";
              return (
                <div key={r.author_id} data-testid={`lb-row-${r.author_id}`} className={`grid grid-cols-[40px_1fr_repeat(4,minmax(48px,1fr))] gap-2 px-4 py-3 border-b border-[#2A3544] last:border-0 tabnums ${r.is_you ? "bg-[#24362A]/40" : ""}`}>
                  <span className="font-display text-white/70">{r.rank <= 3 ? <Medal size={16} className="inline" style={{ color }} /> : r.rank}</span>
                  <span className="flex items-center gap-2 min-w-0">
                    {r.is_character && <img src={`/characters/${r.author_id}_master.png`} alt="" className="w-6 h-6 object-contain" />}
                    <span className="text-white truncate">{r.name || CHAR_NAME[r.author_id] || r.author_id}</span>
                    {r.is_character && <span className="text-[10px] px-1.5 rounded bg-white/10 text-white/50">CHAR</span>}
                  </span>
                  <span className="text-right text-white/70">{rec.su_w}-{rec.su_l}</span>
                  <span className="text-right text-white/70">{rec.ats_w}-{rec.ats_l}</span>
                  <span className="text-right text-white/70">{rec.ou_w}-{rec.ou_l}</span>
                  <span className="text-right text-white font-medium">{rec.points ?? 0}</span>
                </div>
              );
            })}
          </div>
        )}
      <p className="mt-4 text-[11px] text-white/30 flex items-center gap-1"><Trophy size={12} /> Records grade against final results using server-authoritative scoring.</p>
    </div>
  );
}
